import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createManualQuote,
  type CreateQuoteRequest,
  type CreateQuoteResult,
} from '@/lib/api';

/**
 * useCreateQuote — cold owner-handled call entry point. Used when the
 * customer doesn't yet exist in the PWA Conversations list because
 * Shelby never touched the call (cellular call to personal cell,
 * walk-in, etc.).
 *
 * Calls POST /pwa/quotes/new which:
 *   1. find-or-creates the GHL contact by phone (upsertContactByPhone)
 *   2. writes the same 6 fields the in-thread Log Quote writes
 *   3. tags manual_quote_logged + optionally voice_ai_quote_recap_needed_v2
 *   4. fires the v2 Recap + Drip path Shelby's analyzer would have used
 *
 * After success the contact's thread appears in the Conversations list
 * on next refetch — either from the recap SMS materializing a
 * conversation row, or from the contact being indexable. We invalidate
 * the conversations list so the new row shows up without waiting for a
 * stale-time tick.
 */
export function useCreateQuote(slug: string) {
  const qc = useQueryClient();

  return useMutation<
    CreateQuoteResult,
    Error,
    Omit<CreateQuoteRequest, 'slug'>
  >({
    mutationFn: (args) =>
      createManualQuote({
        ...args,
        slug,
      }),
    onSuccess: () => {
      // Invalidate the entire conversations list so the new contact's
      // thread materializes the moment GHL indexes it (typically a
      // few seconds after the recap SMS fires).
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
      // Also invalidate the "today" + attention counts so the owner's
      // dashboard reflects the new quote immediately.
      void qc.invalidateQueries({ queryKey: ['today', slug] });
      void qc.invalidateQueries({ queryKey: ['attention-count', slug] });
    },
  });
}
